import Vue, { computed, reactive, ref, toRef } from "vue";
import type { RawLocation, Route, RouterOptions } from "vue-router";
import Router from "vue-router";
import { buildRouteItems } from "@/app/ui/navigation/menuItems";
import type { IRouteItem } from "@/app/ui/navigation/types";
import NotFound from "@/components/pages/NotFound.vue";
import userStore from "@/store/modules/userStore";
import { BrewManError } from "@/app/errors";
import { useI18n } from "@/plugins/i18n";
import versionManager from "@/store/modules/versionManager";
import { signInWithQuery } from "@/app/ui/navigation/accountManagement";
import type {
  Dictionary,
  Location,
  RouteConfig,
} from "vue-router/types/router";
import type { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import { unauthorisedMenu } from "@/app/ui/navigation/unauthorisedMenu";
import { showUnauthorisedDialog } from "@/components/composables/promptDialogs";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import { isNotNullOrUndefined } from "@/app/apiutils";
import eventShipper from "@/app/eventlogging/eventShipper";

Vue.use(Router);

export const pageCounter = ref(0);
export const query = ref<Dictionary<string | (string | null)[]>>();

const { $t } = useI18n();

const routerOptions: RouterOptions = {
  mode: "history",
};

// Make the router reactive so we can detect updates to 'currentRoute'
// @ts-ignore
const router = reactive(new Router(routerOptions)) as Router;

export const configureRouterForBm = () => {
  const routes: RouteConfig[] = [
    { path: "/", redirect: "/home" },
    {
      name: "distributionapp_redirect",
      path: "/distributionapp",
      beforeEnter: () => {
        // If the window is already open, just open it.
        const distributionAppWindow = window.open("", "distributionapp");
        if (!distributionAppWindow) {
          // Handle has failed (window is closed or blocked).
          // allow the user to open the window manually.
          window.location.href = "/distributionapp/";
        } else if (
          distributionAppWindow.location.pathname !== "/distributionapp/"
        ) {
          // The tab is open, but not on the correct page.
          distributionAppWindow.location.href = "/distributionapp/";
          distributionAppWindow.focus();
        }
      },
    },
    // Fall-through 404
    { path: "*", name: "not-found", component: NotFound },
  ];

  // The routes for the menu items are added via the UI component.
  const routeAdder = (ri: IRouteItem): void => {
    // TODO - make url mandatory for routes once they are properly split from menus.
    if (ri.url) {
      routes.push({
        name: ri.childRoutes.some((r) => r.path === "") ? undefined : ri.name, // dont set a name if any of our child routes have a default path
        path: ri.url,
        component: ri.view,
        meta: {
          ...ri.routeMetadata,
          requiredUserPermissions: ri.requiredUserPermissions,
        },
        children: ri.childRoutes,
        props: ri.componentProps,
        redirect: ri.redirect,
      });
    }

    if (ri.subMenus) {
      ri.subMenus.forEach(routeAdder);
    }
  };

  buildRouteItems().forEach(routeAdder);

  for (const route of routes) {
    router.addRoute(route);
  }

  // This guard will ensure the router forwards to the signin page if the user is not logged in.
  router.beforeEach(async (to, from, next): Promise<any> => {
    // Check to see if our view of the server version is consistent with the latest API calls
    // if not perform a full refresh.
    if (versionManager.requiresRefresh()) {
      location.href = to.fullPath;
    }

    // Check for logged in state.
    if (!to.meta?.preSession && to.query.token) {
      const token = to.query.token;
      delete to.query.token;

      if (to.name === null) {
        throw new BrewManError("Unexpected null route name");
      }

      const ret = router.resolve({
        name: to.name,
        params: to.params,
        query: to.query,
        hash: to.hash,
        path: to.path,
      });

      next(
        signInWithQuery({
          redirect: ret.resolved.fullPath,
          token,
        })
      );
      return;
    }

    // Not authenticated, ask to sign in
    if (
      !to.meta?.preSession &&
      !userStore.isAuthenticatedWithSelectedTenant.value
    ) {
      userStore.logSessionState("route not authenticated");
      await eventShipper.flush(2000);

      next(signInWithQuery({ redirect: to.fullPath }));
      return;
    }

    // For all matched routes (where this will include parent routes, where we have child route definitions)
    const requiredPermissions: EnumUserPermission[] = to.matched
      .flatMap(
        (matchedRoute): EnumUserPermission[] | undefined =>
          matchedRoute.meta.requiredUserPermissions
      )
      .filter(isNotNullOrUndefined);

    // If we have permissions that are required for the route
    if (requiredPermissions.length) {
      const userHasPermissions =
        hasPermissionsUsingUserStore(requiredPermissions);

      if (!userHasPermissions) {
        // We are coming from a URL that isn't within brewman, or go directly to the URL
        if (!from.matched.length) {
          next(
            unauthorisedMenu.landingLocation({
              requiredPermissions,
            })
          );
          return;
        }

        // Internal redirects should get caught here
        await showUnauthorisedDialog({ requiredPermissions });
        return;
      }
    }

    pageCounter.value++;
    next();
  });

  addCommonBmTpRouterAfterEach();
  useCommonBmTpRouterBackWithFallback();
};

export default router;

export const addCommonBmTpRouterAfterEach = () => {
  router.afterEach((to: Route): any => {
    query.value = to.query;
  });
};

export const useCommonBmTpRouterBackWithFallback = () => {
  /**
   * Will navigate to the previous page if it's within our application.
   * @param location to fallback to if we cannot go back safely.
   */
  router.backWithFallback = async (
    location: RawLocation = { path: "/" }
  ): Promise<Route | void> => {
    if (pageCounter.value > 1) {
      router.back();
    } else {
      return router.push(location);
    }
  };
};

export const useRouter = () => {
  return {
    router,
    route: toRef(router, "currentRoute"),
  };
};

export const useRouterQueryParams = <T extends string[]>(...items: T) => {
  type QueryParams = {
    [K in (typeof items)[number]]: string | undefined;
  };

  type ParamsToChange<P> = { [K in keyof P]?: string };

  const queryParams = computed(
    () =>
      Object.fromEntries(
        items.map((item) => [
          item,
          query.value ? (query.value[item] as string | undefined) : undefined,
        ])
      ) as QueryParams
  );

  async function updateQueryParams<P extends QueryParams>(
    paramsToChange: ParamsToChange<P>
  ) {
    await router.push({
      path: router.currentRoute.fullPath,
      query: paramsToChange,
    });
  }

  return {
    queryParams,
    updateQueryParams,
  };
};

export const pushRouteOrNewWindow = async (
  location: Location,
  event: {
    ctrlKey: boolean;
    metaKey: boolean;
  }
) => {
  if (event.ctrlKey || event.metaKey) {
    window.open(router.resolve(location).href, "_blank");
  } else {
    await router.push(location);
  }
};
// Vue router does not support component hooks in setup for vue 2. For progress:
// https://github.com/vuejs/composition-api/issues/49#issuecomment-701015268
